/**
 * Hints Pattern Component
 * Manages a carousel of hint elements with pager navigation
 */

class Hints extends HTMLElement {
	#elements;
	#currentPage;
	#totalPages;
	#touchStartX = 0;
	#touchStartY = 0;
	#touchEndX = 0;
	#isDragging = false;

	/**
	 * Set up hints custom element
	 */
	constructor() {
		super();

		this.#currentPage = 1;
		this.#totalPages = parseInt(this.dataset.totalPages || "1", 10);

		this.#elements = {
			track: /** @type {HTMLDivElement} */ (this.querySelector(".Hints-track")),
			slides: /** @type {NodeListOf<HTMLDivElement>} */ (
				this.querySelectorAll(".Hints-slide")
			),
			pager: /** @type {HTMLElement} */ (this.querySelector(".Pager")),
		};

		this.#init();
	}

	/**
	 * Initialize component
	 */
	#init() {
		// Set initial active state
		this.#updateActiveSlide();
		this.#updatePagerState();

		this.#registerObservers();

		// Add event listeners
		this.#addEventListeners();
	}

	#handleArticleIntroIntersectionChange(entries) {
		entries.forEach((entry) => {
			if (!entry.isIntersecting) {
				this.classList.remove("is-relative");
				this.classList.add("is-fixed");
			} else {
				this.classList.remove("is-fixed");
				this.classList.add("is-relative");
			}
		});
	}

	#handleArticleEndIntersectionChange(entries) {
		entries.forEach((entry) => {
			if (entry.isIntersecting && this.classList.contains("is-fixed")) {
				this.classList.add("is-hidden");
			} else if (
				!entry.isIntersecting &&
				this.classList.contains("is-hidden")
			) {
				this.classList.remove("is-hidden");
			}
		});
	}

	#articleIntroObserver() {
		const articleIntro = document.querySelector(".ArticleIntro");

		if (!articleIntro) {
			return;
		}

		const options = {
			threshold: 0.7,
		};

		const observer = new IntersectionObserver(
			this.#handleArticleIntroIntersectionChange.bind(this),
			options,
		);
		observer.observe(articleIntro);
	}

	#articleEndObserver() {
		let observedTarget = document.querySelector(".js-RelatedArticles");

		if (!observedTarget) {
			observedTarget = document.querySelector(".js-Template-footer");
		}

		if (!observedTarget) {
			return;
		}

		const options = {
			threshold: 0.3,
		};

		const observer = new IntersectionObserver(
			this.#handleArticleEndIntersectionChange.bind(this),
			options,
		);
		observer.observe(observedTarget);
	}

	#registerObservers() {
		this.#articleIntroObserver();
		this.#articleEndObserver();
	}

	/**
	 * Add event listeners
	 */
	#addEventListeners() {
		// Listen for pager navigation events
		if (this.#elements.pager) {
			this.#elements.pager.addEventListener("click", (event) => {
				this.#handlePagerClick(event);
			});
		}

		// Keyboard navigation
		this.addEventListener("keydown", (event) => {
			this.#handleKeydown(event);
		});

		// Touch/swipe navigation
		if (this.#elements.track) {
			this.#elements.track.addEventListener(
				"touchstart",
				(event) => {
					this.#handleTouchStart(event);
				},
				{ passive: true },
			);

			this.#elements.track.addEventListener("touchmove", (event) => {
				this.#handleTouchMove(event);
			});

			this.#elements.track.addEventListener("touchend", () => {
				this.#handleTouchEnd();
			});
		}
	}

	/**
	 * Handle pager click events
	 * @param {MouseEvent} event
	 */
	#handlePagerClick(event) {
		event.preventDefault();

		const button = event.target.closest(".Pager-button");
		if (!button) return;

		if (button.classList.contains("Pager-button--disabled")) return;

		let targetPage = this.#currentPage;

		if (button.classList.contains("Pager-button--prev")) {
			targetPage = Math.max(1, this.#currentPage - 1);
		} else if (button.classList.contains("Pager-button--next")) {
			targetPage = Math.min(this.#totalPages, this.#currentPage + 1);
		}

		this.#goToPage(targetPage);
	}

	/**
	 * Handle keyboard navigation
	 * @param {KeyboardEvent} event
	 */
	#handleKeydown(event) {
		// Don't intercept arrow keys when the user is typing in a form field
		if (event.target.matches("input, textarea, select, [contenteditable]")) {
			return;
		}

		// Only handle if pager or hints container is focused
		if (
			!event.target.closest(".Hints-pager") &&
			!event.target.closest(".Hints-track")
		) {
			return;
		}

		switch (event.key) {
			case "ArrowLeft":
				event.preventDefault();
				this.#goToPage(Math.max(1, this.#currentPage - 1));
				break;
			case "ArrowRight":
				event.preventDefault();
				this.#goToPage(Math.min(this.#totalPages, this.#currentPage + 1));
				break;
			case "Home":
				event.preventDefault();
				this.#goToPage(1);
				break;
			case "End":
				event.preventDefault();
				this.#goToPage(this.#totalPages);
				break;
		}
	}

	/**
	 * Handle touch start
	 * @param {TouchEvent} event
	 */
	#handleTouchStart(event) {
		this.#touchStartX = event.touches[0].clientX;
		this.#touchStartY = event.touches[0].clientY;
		this.#touchEndX = event.touches[0].clientX;
		this.#isDragging = true;
	}

	/**
	 * Handle touch move
	 * @param {TouchEvent} event
	 */
	#handleTouchMove(event) {
		if (!this.#isDragging) return;

		this.#touchEndX = event.touches[0].clientX;

		// Prevent scrolling if horizontal swipe is detected
		const deltaX = Math.abs(this.#touchEndX - this.#touchStartX);
		const deltaY = Math.abs(event.touches[0].clientY - this.#touchStartY);

		if (deltaX > deltaY && deltaX > 10) {
			event.preventDefault();
		}
	}

	/**
	 * Handle touch end
	 */
	#handleTouchEnd() {
		if (!this.#isDragging) return;
		this.#isDragging = false;

		const swipeDistance = this.#touchStartX - this.#touchEndX;
		const swipeThreshold = 50; // Minimum pixels to trigger a swipe

		// Swipe left (next slide)
		if (swipeDistance > swipeThreshold) {
			this.#goToPage(Math.min(this.#totalPages, this.#currentPage + 1));
		}
		// Swipe right (previous slide)
		else if (swipeDistance < -swipeThreshold) {
			this.#goToPage(Math.max(1, this.#currentPage - 1));
		}

		// Reset touch positions
		this.#touchStartX = 0;
		this.#touchEndX = 0;
	}

	/**
	 * Navigate to a specific page
	 * @param {number} pageNumber - Page number (1-indexed)
	 * @param {boolean} animate - Whether to animate the transition
	 */
	#goToPage(pageNumber, animate = true) {
		if (pageNumber === this.#currentPage) return;
		if (pageNumber < 1 || pageNumber > this.#totalPages) return;

		const oldPage = this.#currentPage;
		this.#currentPage = pageNumber;

		// Close all expanded hints before navigation
		const expandedHints = this.querySelectorAll("cd-hint.is-expanded");
		expandedHints.forEach((hint) => {
			hint.classList.remove("is-expanded");
			const button = hint.querySelector(".Hint-header");
			if (button) {
				button.setAttribute("aria-expanded", "false");
			}
		});

		// Update data attributes
		this.dataset.currentPage = String(pageNumber);

		// Calculate transform
		const slideWidth = this.#elements.slides[0]?.offsetWidth || 0;

		// Get the actual gap from computed styles
		const trackStyles = window.getComputedStyle(this.#elements.track);
		const gap = parseFloat(trackStyles.gap) || 32;

		const offset = (slideWidth + gap) * (pageNumber - 1);

		// Apply transform
		if (this.#elements.track) {
			if (!animate) {
				this.#elements.track.style.transition = "none";
			}

			this.#elements.track.style.transform = `translateX(-${offset}px)`;

			if (!animate) {
				// Force reflow to ensure transition is removed before re-enabling
				this.#elements.track.offsetHeight;
				this.#elements.track.style.transition = "";
			}
		}

		// Update active slide state
		this.#updateActiveSlide();

		// Update pager state
		this.#updatePagerState();

		// Dispatch custom event
		this.dispatchEvent(
			new CustomEvent("hints:change", {
				detail: {
					oldPage,
					newPage: pageNumber,
					totalPages: this.#totalPages,
				},
				bubbles: true,
			}),
		);
	}

	/**
	 * Update active slide visual state
	 */
	#updateActiveSlide() {
		this.#elements.slides.forEach((slide, index) => {
			slide.classList.toggle(
				"Hints-slide--active",
				index + 1 === this.#currentPage,
			);
		});
	}

	/**
	 * Update pager visual state
	 */
	#updatePagerState() {
		if (!this.#elements.pager) return;

		// Update pager data attribute
		this.#elements.pager.dataset.currentPage = String(this.#currentPage);

		// Update position indicator dots if present
		const positionIndicator =
			this.#elements.pager.querySelector(".PositionIndicator");
		if (positionIndicator) {
			const dots = positionIndicator.querySelectorAll(".PositionIndicator-dot");
			dots.forEach((dot, index) => {
				dot.classList.toggle(
					"PositionIndicator-dot--active",
					index + 1 === this.#currentPage,
				);
			});
		}

		// Update prev/next button states
		const prevButton = this.#elements.pager.querySelector(
			".Pager-button--prev",
		);
		const nextButton = this.#elements.pager.querySelector(
			".Pager-button--next",
		);

		if (prevButton) {
			const isDisabled = this.#currentPage === 1;
			prevButton.classList.toggle("Pager-button--disabled", isDisabled);
			prevButton.setAttribute("aria-disabled", isDisabled ? "true" : "false");
			if (isDisabled) {
				prevButton.setAttribute("tabindex", "-1");
			} else {
				prevButton.removeAttribute("tabindex");
			}
		}

		if (nextButton) {
			const isDisabled = this.#currentPage === this.#totalPages;
			nextButton.classList.toggle("Pager-button--disabled", isDisabled);
			nextButton.setAttribute("aria-disabled", isDisabled ? "true" : "false");
			if (isDisabled) {
				nextButton.setAttribute("tabindex", "-1");
			} else {
				nextButton.removeAttribute("tabindex");
			}
		}
	}

	/**
	 * Get current page number
	 * @returns {number}
	 */
	getCurrentPage() {
		return this.#currentPage;
	}

	/**
	 * Get total pages
	 * @returns {number}
	 */
	getTotalPages() {
		return this.#totalPages;
	}
}

if (!customElements.get("cd-hints")) {
	customElements.define("cd-hints", Hints);
}
